/**
 * WebSocket 客户端 —— 进程内单例。
 *
 * 职责:
 * 1. 建连 & 鉴权 (token 通过 query string,因为浏览器无法在 WS upgrade 请求里塞自定义 header);
 * 2. 自动重连 (指数退避,1s → 2s → 4s → 8s → 10s 封顶);
 * 3. 离线消息队列 (未 OPEN 时 send 入队,OPEN 后 flush);
 * 4. 帧分发 —— 调用方注册 (type, handler) listener。
 *
 * 不感知业务: 不知道 store / router 是什么, 仅原始帧进出。
 * 业务接线在 composables/useWebSocket.ts。
 */

import { ref, type Ref } from 'vue'

import type { ClientFrame, ServerFrame } from '@/ws/frames'

export type WSStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed'

type FrameType = ServerFrame['type']
type FrameOfType<T extends FrameType> = Extract<ServerFrame, { type: T }>
type Handler<T extends FrameType> = (frame: FrameOfType<T>) => void
type AnyHandler = (frame: ServerFrame) => void

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 10000] as const
const MAX_QUEUE_SIZE   = 64
const PING_TIMEOUT_MS  = 25_000

export class WSClient {
  readonly status: Ref<WSStatus> = ref('idle')

  private ws:                WebSocket | null = null
  private token:             string | null    = null
  /** voluntaryClose 为 true 时, 不再尝试重连 (logout 路径)。*/
  private voluntaryClose                      = false
  private reconnectAttempt                    = 0
  private reconnectTimer:    number | null    = null
  private pingWatchdog:      number | null    = null
  private _visibilityHandler: (() => void) | null = null
  private outQueue:          string[]         = []
  /** 内部以 AnyHandler 存储, 在 on() 入口做类型擦除 (调用方已经通过 T 约束保证安全)。*/
  private listeners:         Map<FrameType, Set<AnyHandler>> = new Map()

  /** 启动一个新连接。若已连接,先 disconnect。token 通过 query string 传。*/
  connect(token: string): void {
    if (this.ws && this.token === token) {
      // 同 token 重复 connect, 视为 no-op
      return
    }
    this.disconnect()
    this.token            = token
    this.voluntaryClose   = false
    this.reconnectAttempt = 0
    this._installOnlineListener()
    this._installVisibilityListener()
    this._open()
  }

  /** 浏览器恢复网络时立即尝试重连一次, 不再傻等指数退避剩余时间。*/
  private _onlineHandler:    (() => void) | null = null

  private _installOnlineListener(): void {
    if (this._onlineHandler) return
    this._onlineHandler = () => {
      if (this.voluntaryClose || !this.token) return
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return
      // 取消挂起的 backoff timer, 立刻重连。reconnectAttempt 不重置 —— 让下一次
      // 失败时仍按递增退避, 避免坏网频繁切换造成连风暴。
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      console.info('[ws] window.online → immediate retry')
      this._open()
    }
    window.addEventListener('online', this._onlineHandler)
  }

  private _removeOnlineListener(): void {
    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler)
      this._onlineHandler = null
    }
  }

  private _resetPingWatchdog(): void {
    if (this.pingWatchdog !== null) {
      window.clearTimeout(this.pingWatchdog)
    }
    this.pingWatchdog = window.setTimeout(() => {
      this.pingWatchdog = null
      if (this.voluntaryClose || !this.token) return
      if (this.status.value !== 'connected') return
      console.info('[ws] ping timeout — force reconnect')
      if (this.ws) {
        this.ws.onopen = null; this.ws.onmessage = null
        this.ws.onerror = null; this.ws.onclose = null
        try { this.ws.close(1000, 'ping timeout') } catch { /* noop */ }
        this.ws = null
      }
      this.reconnectAttempt = 0
      this._open()
    }, PING_TIMEOUT_MS)
  }

  private _clearPingWatchdog(): void {
    if (this.pingWatchdog !== null) {
      window.clearTimeout(this.pingWatchdog)
      this.pingWatchdog = null
    }
  }

  private _installVisibilityListener(): void {
    if (this._visibilityHandler) return
    this._visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return
      if (this.voluntaryClose || !this.token) return
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Socket looks alive but OS may have silently killed it while the app
        // was backgrounded.  Send a probe: if the underlying TCP is dead the
        // send will throw synchronously (or the server will never echo a ping),
        // and the ping-watchdog will fire within PING_TIMEOUT_MS.  We also
        // reset the watchdog so the clock starts from *now* (not from the last
        // message received before backgrounding).
        try { this.ws.send(JSON.stringify({ type: 'ping' })) } catch { /* ignore */ }
        this._resetPingWatchdog()
        return
      }
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
      console.info('[ws] visibilitychange → visible, immediate reconnect')
      this.reconnectAttempt = 0
      this._open()
    }
    document.addEventListener('visibilitychange', this._visibilityHandler)
  }

  private _removeVisibilityListener(): void {
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler)
      this._visibilityHandler = null
    }
  }

  /** 主动断开, 不再重连。logout 时调用。*/
  disconnect(): void {
    this.voluntaryClose = true
    this._clearPingWatchdog()
    this._removeVisibilityListener()
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      // 把 listener 清掉, 避免 close 事件触发重连
      this.ws.onopen    = null
      this.ws.onmessage = null
      this.ws.onerror   = null
      this.ws.onclose   = null
      if (this.ws.readyState <= WebSocket.OPEN) {
        try { this.ws.close(1000, 'client logout') } catch { /* noop */ }
      }
      this.ws = null
    }
    this.token       = null
    this.outQueue    = []
    this.status.value = 'closed'
    this._removeOnlineListener()
  }

  /** 注册帧处理器。返回卸载函数。*/
  on<T extends FrameType>(type: T, handler: Handler<T>): () => void {
    let set = this.listeners.get(type)
    if (!set) {
      set = new Set()
      this.listeners.set(type, set)
    }
    const erased = handler as unknown as AnyHandler
    set.add(erased)
    return () => { set?.delete(erased) }
  }

  /** 发送一帧。未 OPEN 时入队, OPEN 后 flush。*/
  send(frame: ClientFrame): void {
    const text = JSON.stringify(frame)
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(text)
      return
    }
    if (this.outQueue.length >= MAX_QUEUE_SIZE) {
      // 队列溢出, 丢最早一条
      this.outQueue.shift()
    }
    this.outQueue.push(text)
  }

  // ─────────────────────── 内部 ─────────────────────────

  private _open(): void {
    if (!this.token) return
    const isHttps   = location.protocol === 'https:'
    const wsProto   = isHttps ? 'wss' : 'ws'
    const apiBase   = import.meta.env.BASE_URL.replace(/\/$/, '')
    const url       = `${wsProto}://${location.host}${apiBase}/ws?token=${encodeURIComponent(this.token)}`

    this.status.value = this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting'

    const ws = new WebSocket(url)
    this.ws  = ws

    ws.onopen = () => {
      if (this.ws !== ws) return
      this.status.value      = 'connected'
      this.reconnectAttempt  = 0
      this._resetPingWatchdog()
      // flush 队列
      while (this.outQueue.length && ws.readyState === WebSocket.OPEN) {
        ws.send(this.outQueue.shift()!)
      }
    }

    ws.onmessage = (ev) => {
      if (this.ws !== ws) return
      let frame: ServerFrame
      try {
        frame = JSON.parse(ev.data) as ServerFrame
      } catch {
        console.warn('[ws] non-JSON frame:', ev.data)
        return
      }
      this._dispatch(frame)
    }

    ws.onerror = () => {
      // 错误本身不触发任何操作, 紧接着会有 onclose
    }

    ws.onclose = (ev) => {
      if (this.ws !== ws) return
      this.ws = null
      this._clearPingWatchdog()
      // close code 4001 由后端定义 (此项目暂未严格使用),先按通用处理。
      if (this.voluntaryClose) {
        this.status.value = 'closed'
        return
      }
      // 服务端发了 error{bad_token} 后会 close, 监听器会拦截 (logout + voluntaryClose=true)。
      // 如果走到这里说明对方不可达或正常断开, 安排重连。
      const delay = RECONNECT_DELAYS[
        Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)
      ]
      this.reconnectAttempt++
      this.status.value = 'reconnecting'
      this.reconnectTimer = window.setTimeout(() => {
        this.reconnectTimer = null
        this._open()
      }, delay)
      console.info(`[ws] closed (code ${ev.code}), reconnecting in ${delay}ms`)
    }
  }

  private _dispatch(frame: ServerFrame): void {
    if (frame.type === 'ping') {
      this._resetPingWatchdog()
      return
    }
    const set = this.listeners.get(frame.type)
    if (!set) return
    for (const h of set) {
      try {
        h(frame)
      } catch (err) {
        console.error('[ws] handler threw on', frame.type, err)
      }
    }
  }
}

// 进程内单例 —— useWebSocket 通过 module-scope 共享
export const wsClient = new WSClient()
